import { Form, useForm } from '@/components/forms';
import FormCombobox from '@/components/forms/FormCombobox';
import Select from '@/components/forms/Select';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/design-system/button';
import { useAttributes } from '@/hooks/useAttributes';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { TrashIcon } from '@/icons';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import useAddMainPageHost from '../hooks/useAddMainPageHost';

const manageRecommendationSchema = z.object({
  locationId: z.string().min(1, { message: 'Required' }),
  groupId: z.string().min(1, { message: 'Required' }),
  clubId: z.string().min(1, { message: 'Required' }),
  displayOrderId: z.string().min(1, { message: 'Required' }),
  hosts: z
    .array(
      z.object({
        hostId: z.string(),
      }),
    )
    .min(1, { message: 'At least one host is required' })
    .max(3, { message: 'Maximum 3 hosts are allowed' })
    .superRefine((hosts, ctx) => {
      if (!hosts[0] || !hosts[0].hostId || hosts[0].hostId.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Host is required',
          path: [0, 'hostId'],
        });
      }
    }),
});

export const ManageRecommendationForm = ({
  locationId,
  groupId,
  addedClubIds,
  addedDisplayOrders,
}: {
  locationId: string;
  groupId: string;
  addedClubIds: string[];
  addedDisplayOrders: string[];
}) => {
  const form = useForm({
    schema: manageRecommendationSchema,
    mode: 'onBlur',
    defaultValues: {
      locationId: locationId,
      groupId: groupId,
      hosts: [{ hostId: '' }],
    },
  });
  const hostData = useWatch({
    control: form.control,
    name: 'hosts',
  });

  const storeId = useWatch({
    control: form.control,
    name: 'clubId',
  });

  const { t } = useDynamicLocale();
  const { data: locationData } = useAttributes('location');
  const { data: groupData, isLoading: groupLoading } = useDropdown(
    'MainPage',
    locationId,
    undefined,
    'Group',
    !!locationId,
  );
  const { data: storeData, isLoading: storeLoading } = useDropdown(
    'MainPage',
    locationId,
    undefined,
    'Store',
    !!locationId,
  );
  const storeDropdownData = storeData?.filter((s) => !addedClubIds.includes(s.value));
  const { data: hostDropdownData } = useDropdown('MainPage', storeId, undefined, 'Host', !!storeId);
  const { data: displayOrderOptions, isLoading: displayOrderLoading } = useDropdown('displayOrderId');
  const displayOrderDropdownData = displayOrderOptions?.filter((d) => !addedDisplayOrders.includes(d.label));
  const { mutateAsync } = useAddMainPageHost();
  const isLoading = groupLoading || storeLoading || displayOrderLoading;
  return isLoading ? (
    <Spinner />
  ) : (
    <Form
      form={form}
      onSubmit={async (data) => {
        const payload = {
          ...data,
          hosts: data.hosts.filter((h) => h.hostId && h.hostId.trim() !== ''),
        };
        await mutateAsync(payload);
      }}
      className="overflow-y-auto max-h-[553px] scrollbar-none px-0.5"
    >
      <div className="space-y-[18px]">
        <Select
          label="common.form.label.location"
          {...form.register('locationId')}
          placeholder={t('common.form.placeholder.location')}
          options={locationData ?? []}
          disabled
        />
        <div className="grid grid-cols-2 gap-4.5">
          <Select
            label="common.form.label.group"
            {...form.register('groupId')}
            placeholder={t('common.form.placeholder.group')}
            options={groupData ?? []}
            disabled
          />
          <FormCombobox
            label="common.form.label.storeName"
            {...form.register('clubId')}
            placeholder={t('common.form.placeholder.storeName')}
            options={storeDropdownData ?? []}
          />
        </div>
        <Select
          label="common.form.label.displayOrder"
          {...form.register('displayOrderId')}
          placeholder={t('common.form.placeholder.displayOrder')}
          options={displayOrderDropdownData ?? []}
        />
        <div className="flex items-center gap-2">
          <div className="text-foreground text-control font-bold leading-5">{t('common.form.label.host')}</div>
          <div className="h-px flex-1 bg-border-divider"></div>
        </div>
        {hostData?.map((_, index) => (
          <div key={index}>
            {(hostData?.[index]?.hostId || index !== 0) && (
              <div className="w-full flex justify-end">
                <Button
                  variant={'ghost'}
                  onClick={() => {
                    const filtered = hostData.filter((_, hostIndex) => hostIndex !== index);
                    form.setValue('hosts', filtered.length > 0 ? filtered : [{ hostId: '' }]);
                  }}
                  className="p-0 hover:bg-card hover:text-foreground h-[16px] text-[14px] font-normal"
                >
                  <TrashIcon className="size-4 text-destructive-text" /> 削除
                </Button>
              </div>
            )}
            <div className="pb-3 border-b border-dotted border-b-border-divider grid gap-2">
              <Select
                label="common.form.label.host"
                {...form.register(`hosts.${index}.hostId` as const)}
                placeholder={t('common.form.placeholder.host')}
                options={
                  hostDropdownData?.filter(
                    (h) => !hostData.some((selected, i) => i !== index && selected.hostId === h.value),
                  ) ?? []
                }
                isRequired={index === 0 ? true : false}
              />
            </div>
          </div>
        ))}
        {hostData.length < 3 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => form.setValue('hosts', [...(hostData ?? []), { hostId: '' }])}
            className="bg-card border-dashed border-foreground text-[11px] pl-[15px] pr-[17px] py-2 h-[24px] hover:border-foreground hover:text-foreground"
          >
            {t('common.page.button.addNew')}
          </Button>
        )}
      </div>
    </Form>
  );
};
